import { PublicKey, Transaction } from '@solana/web3.js';

const TOKEN_PROGRAMS = new Set([
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
]);
const WITHDRAW_EXCESS = 38;
const LAMPORTS_PER_SOL = 1_000_000_000;

const els = {
  form: document.querySelector('#scan-form'),
  input: document.querySelector('#wallet-input'),
  scanButton: document.querySelector('#scan-button'),
  status: document.querySelector('#status'),
  result: document.querySelector('#result'),
  walletValue: document.querySelector('#wallet-value'),
  scanned: document.querySelector('#scanned'),
  eligible: document.querySelector('#eligible'),
  native: document.querySelector('#native'),
  claimable: document.querySelector('#claimable'),
  futureAdditional: document.querySelector('#future-additional'),
  futureTotal: document.querySelector('#future-total'),
  programSplit: document.querySelector('#program-split'),
  connectButton: document.querySelector('#connect-wallet'),
  claimButton: document.querySelector('#claim-sol'),
  claimPanel: document.querySelector('#claim-panel'),
  claimStatus: document.querySelector('#claim-status'),
  securityWallet: document.querySelector('#security-wallet'),
  providerModal: document.querySelector('#provider-modal'),
  providerList: document.querySelector('#provider-list'),
  modalClose: document.querySelector('#modal-close'),
};

let currentScan = null;
let provider = null;
let connectedWallet = null;
let claiming = false;

function fmtSol(value) {
  return Number(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  });
}

function short(value) {
  return value ? `${value.slice(0, 6)}...${value.slice(-6)}` : '—';
}

function setStatus(message, tone = '') {
  els.status.textContent = message || '';
  els.status.dataset.tone = tone;
}

function setClaimStatus(message, tone = '') {
  els.claimStatus.textContent = message || '';
  els.claimStatus.dataset.tone = tone;
}

function validateAddress(value) {
  try {
    const key = new PublicKey(String(value || '').trim());
    if (!PublicKey.isOnCurve(key.toBytes())) throw new Error();
    return key.toBase58();
  } catch {
    throw new Error('Enter a valid Solana wallet address.');
  }
}

async function api(path, options) {
  const response = await fetch(path, {
    ...options,
    headers: {
      'content-type': 'application/json',
      ...(options?.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Request failed');
  return body;
}

function renderScan(scan) {
  currentScan = scan;
  els.result.hidden = false;
  els.walletValue.textContent = short(scan.wallet);
  els.walletValue.title = scan.wallet;
  els.scanned.textContent = Number(scan.scanned).toLocaleString('en-GB');
  els.eligible.textContent = Number(scan.eligibleCount).toLocaleString('en-GB');
  els.native.textContent = Number(scan.excludedNative).toLocaleString('en-GB');
  els.claimable.textContent = `${fmtSol(scan.claimableSol)} SOL`;
  els.futureAdditional.textContent = `${fmtSol(scan.additionalFutureSol)} SOL`;
  els.futureTotal.textContent = `${fmtSol(scan.futureTotalSol)} SOL`;
  els.programSplit.textContent = `${scan.byProgram.token} Token / ${scan.byProgram.token2022} Token-2022`;
  els.securityWallet.textContent = scan.wallet;
  updateClaimControls();
}

async function scan(wallet, { silent = false } = {}) {
  const valid = validateAddress(wallet);
  els.input.value = valid;
  if (!silent) setStatus('Scanning Token and Token-2022 accounts…');
  els.scanButton.disabled = true;
  try {
    const result = await api(`/api/scan/${encodeURIComponent(valid)}`);
    renderScan(result);
    if (!silent) setStatus('Live Scan Complete.', 'success');
    history.replaceState(null, '', `?wallet=${encodeURIComponent(valid)}`);
    return result;
  } finally {
    els.scanButton.disabled = false;
  }
}

function detectedProviders() {
  const found = [];
  const add = (name, p) => {
    if (!p || found.some((x) => x.provider === p)) return;
    found.push({ name, provider: p });
  };

  add('Phantom', window.phantom?.solana);
  if (window.solana?.isPhantom) add('Phantom', window.solana);
  add('Solflare', window.solflare);
  add('Backpack', window.backpack?.solana || window.backpack);
  return found;
}

function openProviderModal() {
  const providers = detectedProviders();
  els.providerList.replaceChildren();

  if (providers.length === 0) {
    const p = document.createElement('p');
    p.className = 'modal-empty';
    p.textContent = 'No supported Solana wallet was detected in this browser. Open this page inside Phantom, Solflare or Backpack.';
    els.providerList.append(p);
  } else {
    for (const item of providers) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'provider-button';
      button.textContent = item.name;
      button.addEventListener('click', async () => {
        els.providerModal.close();
        await connect(item.provider, item.name);
      });
      els.providerList.append(button);
    }
  }
  els.providerModal.showModal();
}

async function connect(selected, name) {
  setClaimStatus(`Connecting ${name}…`);
  try {
    const response = await selected.connect();
    const pubkey = response?.publicKey || selected.publicKey;
    if (!pubkey) throw new Error('Wallet did not return a public key');
    const address = new PublicKey(pubkey.toString()).toBase58();
    provider = selected;
    connectedWallet = address;
    els.connectButton.textContent = short(address);

    if (!currentScan || currentScan.wallet !== address) {
      setClaimStatus('Connected. Scanning this wallet before enabling claims…');
      await scan(address, { silent: true });
    }
    setClaimStatus('Wallet connected. Claims always return SOL to this same wallet.', 'success');
    updateClaimControls();
  } catch (error) {
    setClaimStatus(error?.message || 'Wallet connection failed.', 'error');
  }
}

function updateClaimControls() {
  const sameWallet = Boolean(currentScan && connectedWallet && currentScan.wallet === connectedWallet);
  els.claimPanel.hidden = !currentScan;
  els.claimButton.disabled = claiming || !sameWallet || !currentScan || currentScan.claimableLamports <= 0;

  if (!currentScan) return;
  if (currentScan.claimableLamports <= 0) {
    els.claimButton.textContent = 'Nothing to claim now';
  } else if (!connectedWallet) {
    els.claimButton.textContent = 'Connect wallet to claim';
  } else if (!sameWallet) {
    els.claimButton.textContent = 'Connect scanned wallet';
  } else if (!claiming) {
    els.claimButton.textContent = `Claim ${fmtSol(currentScan.claimableSol)} SOL`;
  }
}

function verifyClaimTransaction(tx, build, wallet) {
  const feePayer = tx.feePayer?.toBase58();
  if (feePayer !== wallet) throw new Error('Safety check failed: unexpected fee payer.');
  if (tx.instructions.length !== build.instructionCount) {
    throw new Error('Safety check failed: instruction count changed.');
  }

  const allowedSources = new Set(build.sources);
  for (const ix of tx.instructions) {
    if (!TOKEN_PROGRAMS.has(ix.programId.toBase58())) {
      throw new Error('Safety check failed: transaction contains an unknown program.');
    }
    if (ix.data.length !== 1 || ix.data[0] !== WITHDRAW_EXCESS) {
      throw new Error('Safety check failed: transaction contains an unexpected instruction.');
    }
    if (ix.keys.length !== 3) throw new Error('Safety check failed: unexpected accounts.');

    const [source, destination, authority] = ix.keys;
    if (!allowedSources.has(source.pubkey.toBase58()) || !source.isWritable || source.isSigner) {
      throw new Error('Safety check failed: invalid source account.');
    }
    // The destination and authority are deliberately the same wallet.
    // After transaction compilation Solana merges account privileges, so this
    // wallet may appear both writable and signer because it is also fee payer.
    if (destination.pubkey.toBase58() !== wallet || !destination.isWritable) {
      throw new Error('Safety check failed: SOL destination is not your wallet.');
    }
    if (authority.pubkey.toBase58() !== wallet || !authority.isSigner) {
      throw new Error('Safety check failed: authority is not your wallet.');
    }
  }
}

async function waitForConfirmation(signature, timeoutMs = 45_000) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const status = await api(`/api/tx/${encodeURIComponent(signature)}`);
    if (status.err) throw new Error('The transaction failed on Solana.');
    if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') return;
    await new Promise((resolve) => setTimeout(resolve, 1200));
  }
  throw new Error('Transaction was sent but confirmation timed out. Check your wallet before retrying.');
}

async function claimAll() {
  if (claiming || !provider || !connectedWallet || !currentScan) return;
  if (currentScan.wallet !== connectedWallet) {
    setClaimStatus('The connected wallet must match the scanned wallet.', 'error');
    return;
  }
  if (typeof provider.signAndSendTransaction !== 'function') {
    setClaimStatus('This wallet does not expose signAndSendTransaction in the current browser.', 'error');
    return;
  }

  claiming = true;
  updateClaimControls();
  let recovered = 0;
  let batch = 0;

  try {
    while (true) {
      const build = await api('/api/claim/build', {
        method: 'POST',
        body: JSON.stringify({ wallet: connectedWallet }),
      });

      if (build.done) {
        renderScan(build.scan);
        break;
      }

      batch += 1;
      const txBytes = Uint8Array.from(atob(build.transactionBase64), (c) => c.charCodeAt(0));
      const tx = Transaction.from(txBytes);
      verifyClaimTransaction(tx, build, connectedWallet);

      const batchSol = build.grossLamports / LAMPORTS_PER_SOL;
      setClaimStatus(
        `Batch ${batch}: ${build.instructionCount} account${build.instructionCount === 1 ? '' : 's'}, ${fmtSol(batchSol)} SOL gross. Review and approve in your wallet.`,
      );

      const sent = await provider.signAndSendTransaction(tx, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });
      const signature = typeof sent === 'string' ? sent : sent?.signature;
      if (!signature) throw new Error('Wallet did not return a transaction signature.');

      setClaimStatus(`Batch ${batch} sent. Waiting for Solana confirmation…`);
      await waitForConfirmation(signature);
      recovered += build.grossLamports;

      const refreshed = await scan(connectedWallet, { silent: true });
      setClaimStatus(
        `Confirmed. ${fmtSol(recovered / LAMPORTS_PER_SOL)} SOL gross reclaimed so far. ${refreshed.eligibleCount} account${refreshed.eligibleCount === 1 ? '' : 's'} remaining.`,
        'success',
      );

      if (refreshed.claimableLamports <= 0) break;
    }

    const finalScan = await scan(connectedWallet, { silent: true });
    renderScan(finalScan);
    setClaimStatus(
      `Claim complete. ${fmtSol(recovered / LAMPORTS_PER_SOL)} SOL gross reclaimed. Network transaction fees are paid separately by the wallet.`,
      'success',
    );
  } catch (error) {
    setClaimStatus(error?.message || 'Claim stopped.', 'error');
  } finally {
    claiming = false;
    updateClaimControls();
  }
}

els.form.addEventListener('submit', async (event) => {
  event.preventDefault();
  try {
    await scan(els.input.value);
  } catch (error) {
    setStatus(error?.message || 'Scan failed.', 'error');
  }
});

els.connectButton.addEventListener('click', openProviderModal);
els.claimButton.addEventListener('click', claimAll);
els.modalClose.addEventListener('click', () => els.providerModal.close());

const initialWallet = new URLSearchParams(location.search).get('wallet');
if (initialWallet) {
  els.input.value = initialWallet;
  scan(initialWallet).catch((error) => setStatus(error?.message || 'Scan failed.', 'error'));
}
